/** What a breakdown drawer is handed when its figures are read a page at a time.
 *
 * Four screens open the same drawer (Readiness, Scope 1, Scope 2, Scope 3). Each used to load every
 * figure of the window and filter them in the browser, which failed visibly past
 * `FIGURES_LOAD_CEILING`. Each now asks the server for the one cut it needs (`useFiguresPaged`) and
 * hands the drawer the page, the count and the way to more - and this is the one place that turns a
 * read into those props, so the four cannot come to state *Showing N of M* four ways.
 *
 * **The headline is never computed here.** It is the server's total for the same selection
 * (`/inventory`'s scope, category, site or month figure) and is passed through as a string; where
 * the server states none for a cut (a grade, a coverage) the drawer prints no total for a partial
 * list rather than adding up the part it holds.
 */

import type { BreakdownPaging } from "../components/ReceiptDrawer";
import type { Figure, FigureCutRead } from "../api/queries";
import { tonnes } from "../lib/format";

export function pagingOf(read: FigureCutRead): BreakdownPaging {
  return {
    total: read.total,
    canLoadMore: read.canLoadMore,
    ceilingReached: read.ceilingReached,
    onMore: read.loadMore,
    loadingMore: read.loadingMore,
    moreError: read.moreError,
  };
}

/** A figure's share of the window the drawer was read for, as the server's exact decimal string.
 *
 * `/figures` states it as `window_kgco2e` (G-P7-05), by the allocation every total uses, so the
 * rows of a drawer add up to the figure that was clicked. A figure with no share stated (a server
 * older than this client, or a list read without a window) is a whole record and its own share. */
export function shareInWindow(figure: Figure): string {
  return figure.window_kgco2e ?? figure.kgco2e;
}

/** For a record that runs past the edge of the period, what to say beside its row: the days inside
 * and the whole bill's figure. `undefined` for a record wholly inside. The whole figure is the
 * server's string rounded once by `tonnes`; nothing is divided here. */
export function cutNote(figure: Figure): string | undefined {
  const { window_days: inside, record_days: whole } = figure;
  if (inside == null || whole == null || inside >= whole) return undefined;
  return `${String(inside)} of ${String(whole)} days in the period; whole bill ${tonnes(figure.kgco2e)} t`;
}

/** Said under every drawer whose headline is an allocated total. Each row is the record's share of
 * the period (G-P7-05), so the rows add up to the total; a record that runs past the edge of the
 * period says so beside its row, with the whole bill's figure. */
export const WHOLE_RECORDS_NOTE =
  "A bill that runs past the edge of the period is listed with only its days inside it, so the rows add up to the total above.";
